"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHvacRunSessions = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
/**
 * Hook to fetch HVAC compressor run sessions (on→off cycles) for a hub over a
 * date range, optionally scoped to a single HVAC group. Cycles that straddled
 * an hourly boundary are already stitched together by the resolver. Sessions
 * are returned sorted ascending by `runStartTime` (Unix seconds).
 * @param hubId - Hub ID to fetch run sessions for
 * @param groupId - Optional HVAC group ID to scope results to a single system
 * @param startDate - Start date in YYYY-MM-DD format (local/Pacific)
 * @param endDate - End date in YYYY-MM-DD format (local/Pacific)
 * @returns Run sessions, loading state, error, and refetch
 */
const useHvacRunSessions = (hubId, groupId, startDate, endDate) => {
    const [data, setData] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchRunSessions = async () => {
        if (!hubId || !startDate || !endDate) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHvacRunSessions,
                variables: { hubId, groupId, startDate, endDate },
            });
            const sessions = response.data?.getHvacRunSessions || [];
            setData([...sessions].sort((a, b) => a.runStartTime - b.runStartTime));
        }
        catch (err) {
            console.error('Error fetching HVAC run sessions:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch HVAC run sessions');
            setData([]);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchRunSessions();
    };
    (0, react_1.useEffect)(() => {
        fetchRunSessions();
    }, [hubId, groupId, startDate, endDate]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.useHvacRunSessions = useHvacRunSessions;
exports.default = exports.useHvacRunSessions;
